import * as ko from 'knockout';
import { Observable } from 'knockout';
import { cleaningService } from '../../../api/service.cleaning';
import { formatDateTime } from '../../../utils/format';
import { dialog } from '../../app';
import { DialogParams, IdInfo } from '../../elements/bp-dialog';
import { FieldType } from '../../elements/bp-quick-dialog';

class AdminCleaningLogs {
  title: Observable<string>;
  locations: ko.ObservableArray<any>;

  readonly dialog: Observable<DialogParams | null>;
  readonly toast: Observable<string | null>;

  constructor(params: any) {
    this.title = ko.observable('Admin / Cleaning Logs');
    this.locations = ko.observableArray();

    this.dialog = ko.observable(null);
    this.toast = ko.observable(null);

    this.loadCleaningLocations();
  }

  loadCleaningLocations = () => {
    cleaningService
      .getCleaningSetup()
      .then(results => {

        const locations = results.map(r => {

          const equipment = r.equipment.map(e => {
            const tasks = e.tasks.map(t => {

              let frequency = 'No schedule';
              switch (t.frequency) {

                case "1:00:00:00": frequency = "Daily";
                  break;

                  case "7:00:00:00": frequency = "Weekly";
                  break;

                  case "28:00:00:00": frequency = "Monthly";
                  break;

                case "92:00:00:00": frequency = "3 Monthly";
                  break;

                  case "182:00:00:00": frequency = "6 Monthly";
                  break;

                  case "365:00:00:00": frequency = "Yearly";
                  break;
                
                default:
                  frequency = 'No schedule';
              }

              return {
                ...t,
                task: t.task,
                description: t.description,
                frequency: frequency,
                lastTime: t.lastCompletedTime ? formatDateTime(t.lastCompletedTime) : 'Never',
                nextTime: formatDateTime(t.nextScheduleTime) ?? ' - ',
                equipmentId: t.cleaningEquipmentId,
              }
            });

            return {
              ...e,
              tasks: tasks,
              equipmentId: e.cleaningEquipmentId,
            }
          });

          return {
            ...r,
            equipment: equipment,
            locationId: r.cleaningLocationId,
          }
        });

        this.locations(locations);
      })
      .catch(e => console.log(e));
  }

  showCreateLocationDialog = () => {
    const origMessage = 'Add new location or area';
    const message = ko.observable(origMessage);

    const model = {
      name: ko.observable(''),
      description: ko.observable('')
    };

    const nameField: FieldType<string> = {
      title: 'Location Name',
      type: 'text',
      value: model.name
    };

    const descriptionField: FieldType<string> = {
      title: 'Location Description',
      type: 'textarea',
      value: model.description,
    };

    dialog({
      title: 'Create location',
      message: message,
      fields: ko.observableArray([nameField, descriptionField]),
      cancelText: 'Cancel',
      submitText: 'Create',
      submitAction: () => {
        var errors = '';

        if (model.name() === '') {
          errors += 'Name cannot be empty \r\n';
        }

        if (errors !== '') {
          message(`${origMessage} <br><div class="error"> - ${errors} .</div>`);

          setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
          return;
        }

        cleaningService.createLocation(model.name(), model.description())
          .then(result => {
            if (result) {
              dialog(null);

              this.toast('Location created.');

              this.loadCleaningLocations();

              setTimeout(() => {
                this.toast(null);
              }, 5000);
            } else {
              message(origMessage + '<br><div class="error"> - Failed to create location.</div>');

              setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
            }
          });
      }
    });
  }

  showCreateEquipmentDialog = (locationId: number) => {
    const origMessage = 'Add new equipment to location';
    const message = ko.observable(origMessage);

    const model = {
      name: ko.observable(''),
      description: ko.observable('')
    };

    const nameField: FieldType<string> = {
      title: 'Equipment Name',
      type: 'text',
      value: model.name
    };

    const descriptionField: FieldType<string> = {
      title: 'Equipment Description',
      type: 'textarea',
      value: model.description,
    };

    dialog({
      title: 'Create equipment',
      message: message,
      fields: ko.observableArray([nameField, descriptionField]),
      cancelText: 'Cancel',
      submitText: 'Create',
      submitAction: () => {
        var errors = '';

        if (model.name() === '') {
          errors += 'Name cannot be empty \r\n';
        }

        if (errors !== '') {
          message(`${origMessage} <br><div class="error"> - ${errors} .</div>`);

          setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
          return;
        }

        cleaningService.createEquipment(locationId, model.name(), model.description())
          .then(result => {
            if (result) {
              dialog(null);

              this.toast('Equipment created.');

              this.loadCleaningLocations();

              setTimeout(() => {
                this.toast(null);
              }, 5000);
            } else {
              message(origMessage + '<br><div class="error"> - Failed to create equipment.</div>');

              setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
            }
          });
      }
    });
  }

  showCreateTaskDialog = (equipmentId: number) => {
    const origMessage = 'Add new equipment to location';
    const message = ko.observable(origMessage);

    const model = {
      task: ko.observable(''),
      description: ko.observable(''),
      frequency: ko.observable(null)
    };

    const nameField: FieldType<string> = {
      title: 'Task Name',
      type: 'text',
      value: model.task
    };

    const descriptionField: FieldType<string> = {
      title: 'Task Description',
      type: 'textarea',
      value: model.description,
    };

    const frequencyList: IdInfo[] = [{ Id: 1, Name: 'Daily' }, { Id: 7, Name: 'Weekly' }, { Id: 28, Name: 'Monthly (28 Days)' }, { Id: 92, Name: '3 Monthly' }, { Id: 182, Name: '6 Monthly' }, { Id: 365, Name: 'Yearly' }];

    const frequencyField: FieldType<IdInfo> = {
      title: 'Schedule Frequency',
      type: 'dropdown',
      value: model.frequency,
      options: frequencyList,
      optionsText: (o: any) => o && o.Name,
      optionsValue: (o: any) => o && o.Id,
      optionsCaption: ko.observable('Not Scheduled')
    };

    dialog({
      title: 'Create task',
      message: message,
      fields: ko.observableArray([nameField, descriptionField, frequencyField]),
      cancelText: 'Cancel',
      submitText: 'Create',
      submitAction: () => {
        var errors = '';

        if (model.task() === '') {
          errors += 'Task name cannot be empty \r\n';
        }

        if (model.description() === '') {
          errors += 'Description cannot be empty \r\n';
        }

        if (errors !== '') {
          message(`${origMessage} <br><div class="error"> - ${errors} .</div>`);

          setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
          return;
        }

        cleaningService.createTask(equipmentId, model.task(), model.description(), model.frequency())
          .then(result => {
            if (result) {
              dialog(null);

              this.toast('Task created.');

              this.loadCleaningLocations();

              setTimeout(() => {
                this.toast(null);
              }, 5000);
            } else {
              message(origMessage + '<br><div class="error"> - Failed to create task.</div>');

              setTimeout(() => { this.toast(null); message(origMessage) }, 5000);
            }
          });
      }
    });
  }

}

export default {
  name: 'bp-admin-cleaningLogs',
  viewModel: AdminCleaningLogs,
  template: require('./cleaningLogs.html')
};
